const STORAGE_KEY =
  "nexgbits-pos-runtime";

// ============================================================
// SAVE
// ============================================================

function save(
  state
) {
  if (!state) {
    return;
  }

  const snapshot = {
    terminalId:
      state.terminal?.id
      ??
      state.terminal?.terminalId
      ??
      null,

    sessionId:
      state.session?.id
      ??
      state.session?.sessionId
      ??
      null,

    // ========================================================
    // CART
    //
    // Persist only the normalized values required to rebuild
    // the cart and recalculate totals after refresh.
    // ========================================================

    cart:
      Array.isArray(state.cart)
        ? state.cart.map(
            item => ({
              itemId:
                item.itemId
                ??
                item.id
                ??
                null,

              itemCode:
                item.itemCode
                ??
                item.code
                ??
                item.sku
                ??
                "",

              description:
                item.description
                ??
                item.itemName
                ??
                item.name
                ??
                "Product",

              image:
                item.image
                ??
                item.imageUrl
                ??
                item.imagePath
                ??
                null,

              unitPrice:
                Number(
                  item.unitPrice
                  ??
                  item.sellingPrice
                  ??
                  item.price
                  ??
                  0
                ),

              quantity:
                Math.max(
                  1,
                  Number(
                    item.quantity
                    ??
                    1
                  )
                )
            })
          )
        : [],

    // ========================================================
    // CUSTOMER
    // ========================================================

    customer:
      state.customer
      ??
      null,

    // ========================================================
    // CURRENCY
    // ========================================================

    currency:
      state.currency
      ??
      "cad",

    // ========================================================
    // DISCOUNT
    // ========================================================

    discountRate:
      state.discountRate
      ??
      null,

    discountAmount:
      state.discountAmount
      ??
      null,

    // ========================================================
    // TAX
    // ========================================================

    taxCodeId:
      state.taxCodeId
      ??
      null,

    // ========================================================
    // TIP
    // ========================================================

    tipRate:
      state.tipRate
      ??
      null,

    tipAmount:
      state.tipAmount
      ??
      null,

    // ========================================================
    // SALE
    // ========================================================

    saleId:
      state.sale?.id
      ??
      null,

    // ========================================================
    // CHECKOUT
    // ========================================================

    checkoutDialog:
      Boolean(
        state.checkoutDialog
      ),

    // ========================================================
    // PAYMENT
    // ========================================================

    paymentMethod:
      state.paymentMethod
      ??
      null
  };

  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(
        snapshot
      )
    );
  }
  catch (error) {
    console.error(
      "[POS] Unable to persist POS runtime state:",
      error
    );
  }
}

// ============================================================
// LOAD
// ============================================================

function load() {
  const raw =
    sessionStorage.getItem(
      STORAGE_KEY
    );

  if (!raw) {
    return null;
  }

  try {
    const snapshot =
      JSON.parse(
        raw
      );

    // ========================================================
    // NORMALIZE CART
    // ========================================================

    if (
      Array.isArray(
        snapshot.cart
      )
    ) {
      snapshot.cart =
        snapshot.cart
          .filter(
            item =>
              item &&
              (
                item.itemId !== null &&
                item.itemId !== undefined &&
                item.itemId !== ""
              )
          )
          .map(
            item => ({
              itemId:
                item.itemId
                ??
                item.id
                ??
                null,

              itemCode:
                item.itemCode
                ??
                item.code
                ??
                item.sku
                ??
                "",

              description:
                item.description
                ??
                item.itemName
                ??
                item.name
                ??
                "Product",

              image:
                item.image
                ??
                item.imageUrl
                ??
                item.imagePath
                ??
                null,

              unitPrice:
                normalizeMoney(
                  item.unitPrice
                  ??
                  item.sellingPrice
                  ??
                  item.price
                  ??
                  0
                ),

              quantity:
                normalizeQuantity(
                  item.quantity
                )
            })
          );
    }
    else {
      snapshot.cart =
        [];
    }

    // ========================================================
    // NORMALIZE TRANSACTION VALUES
    // ========================================================

    snapshot.customer =
      snapshot.customer
      ??
      null;

    snapshot.currency =
      String(
        snapshot.currency
        ??
        "cad"
      )
        .trim()
        .toLowerCase();

    snapshot.discountRate =
      normalizeNullableNumber(
        snapshot.discountRate
      );

    snapshot.discountAmount =
      normalizeNullableMoney(
        snapshot.discountAmount
      );

    snapshot.taxCodeId =
      snapshot.taxCodeId
      ??
      null;

    snapshot.tipRate =
      normalizeNullableNumber(
        snapshot.tipRate
      );

    snapshot.tipAmount =
      normalizeNullableMoney(
        snapshot.tipAmount
      );

    snapshot.saleId =
      snapshot.saleId
      ??
      null;

    snapshot.checkoutDialog =
      Boolean(
        snapshot.checkoutDialog
      );

    snapshot.paymentMethod =
      snapshot.paymentMethod
      ??
      null;

    snapshot.terminalId =
      snapshot.terminalId
      ??
      null;

    snapshot.sessionId =
      snapshot.sessionId
      ??
      null;

    return snapshot;
  }
  catch (error) {
    console.error(
      "[POS] Unable to restore persisted POS runtime state:",
      error
    );

    clear();

    return null;
  }
}

// ============================================================
// CLEAR
// ============================================================

function clear() {
  try {
    sessionStorage.removeItem(
      STORAGE_KEY
    );
  }
  catch (error) {
    console.error(
      "[POS] Unable to clear persisted POS runtime state:",
      error
    );
  }
}

// ============================================================
// NORMALIZE MONEY
// ============================================================

function normalizeMoney(
  value
) {
  const numeric =
    Number(
      value
      ??
      0
    );

  if (
    !Number.isFinite(
      numeric
    )
  ) {
    return 0;
  }

  return Math.round(
    (
      numeric +
      Number.EPSILON
    ) *
    100
  ) /
  100;
}

// ============================================================
// NORMALIZE NULLABLE MONEY
// ============================================================

function normalizeNullableMoney(
  value
) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  return normalizeMoney(
    value
  );
}

// ============================================================
// NORMALIZE NUMBER
// ============================================================

function normalizeNullableNumber(
  value
) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const numeric =
    Number(
      value
    );

  return Number.isFinite(
    numeric
  )
    ? numeric
    : null;
}

// ============================================================
// NORMALIZE QUANTITY
// ============================================================

function normalizeQuantity(
  value
) {
  const numeric =
    Number(
      value
      ??
      1
    );

  if (
    !Number.isFinite(
      numeric
    ) ||
    numeric <= 0
  ) {
    return 1;
  }

  return numeric;
}

// ============================================================
// EXPORT
// ============================================================

export default {
  save,
  load,
  clear
};